import {
  DECOY_BAND,
  DECOY_FLIGHT_S,
  DECOY_HIT_SCORE,
  DECOY_MISS_KEY,
  DECOY_MAX_ON_SCREEN,
  DECOY_REACTION_KEY,
  DECOY_SPAWN_FROM,
  DECOY_SPAWN_S,
} from '../../data/boss';
import { PLAYER_HITBOX, SIM_HZ, VIEW_WIDTH } from '../../data/tuning';
import { createAabb } from '../collision';
import { isCollectable } from '../pickups';
import { createRng, type Rng } from '../rng';
import type { LevelRun } from '../level-runner';
import type { TickContext } from '../tick';
import { gapAnchor } from './attacks';
import { cellX, cellY, colOfX } from './chess';
import { JEFE_3 } from '../../data/boss-objects';
import { bossBody } from './boss-fight';
import { hitBossInRun, isVulnerable } from './boss-run';

/**
 * Decoys of the boss fights (GDD §9.1, issue #90). The pilot never shoots: a flour bag spawns every 6 s, the
 * plane picks it up and it flies by itself to the current position of the boss, arriving exactly 0.8 s later.
 *
 *   on-screen (scrolls with the world) > in-flight (48 ticks) > hit | missed
 *
 * A hit takes 1 HP, scores DECOY_HIT_SCORE and triggers the comic reaction; if the boss is not in a vulnerable
 * phase when it arrives (a phase transition) the decoy is lost. Pure and deterministic: positions come from
 * a PRNG seeded with the run seed (GDD §15.3), time is counted in ticks, so a pause freezes everything.
 */
export const DECOY_SPAWN_TICKS = Math.round(DECOY_SPAWN_S * SIM_HZ);
export const DECOY_FLIGHT_TICKS = Math.round(DECOY_FLIGHT_S * SIM_HZ);
/** A decoy that is this far past the left edge of the view is gone. */
const LEFT_MARGIN = 1;
/** Draws of the spawn height before giving up on avoiding the body of the boss. */
const SPAWN_TRIES = 8;

export interface Decoy {
  state: 'on-screen' | 'in-flight';
  x: number;
  y: number;
  /** Where the flight started. */
  fromX: number;
  fromY: number;
  ticksToImpact: number;
}

export interface DecoyField {
  decoys: Decoy[];
  /** Ticks since the arena started or since the last decoy disappeared. */
  spawnTicks: number;
  readonly seed: number;
  rng: Rng;
  /** Every spawn height, in order (determinism checks and the test hooks). */
  spawned: { x: number; y: number }[];
  /** Tick of the last decoy hit: the HUD shows the comic reaction for a while from it (issue #96). */
  lastHitTick: number | null;
}

export function createDecoyField(seed: number): DecoyField {
  return { decoys: [], spawnTicks: 0, seed, rng: createRng(seed), spawned: [], lastHitTick: null };
}

/** Back to the start of the fight: no decoys and the 6 s counter at 0 (a life lost, GDD §6.2 rule 5). */
export function resetDecoys(field: DecoyField): void {
  field.decoys = [];
  field.spawnTicks = 0;
  field.rng = createRng(field.seed);
  field.spawned = [];
  field.lastHitTick = null;
}

/** A height in the reachable band that does not start inside the body of the boss (or the last try). */
function spawnHeight(run: LevelRun, field: DecoyField, x: number): number {
  const boss = run.boss!;
  const body = bossBody(boss);
  let y = 0;
  for (let i = 0; i < SPAWN_TRIES; i++) {
    y = DECOY_BAND.low + field.rng() * (DECOY_BAND.high - DECOY_BAND.low);
    const inside = Math.abs(y - body.y) < body.height / 2 + 0.6 && Math.abs(x - body.x) < body.width / 2 + 0.6;
    if (!inside) return y;
  }
  return y;
}

/** The centre of a free cell of the board column of x (JEFE-3, #121): a row with no piece in it; null if every row is taken. */
function freeCell(objects: readonly { piece?: { row: number; col: number } }[], x: number, field: DecoyField): { x: number; y: number } | null {
  const col = colOfX(x);
  const rows = Array.from({ length: JEFE_3.rows }, (_, row) => row).filter((row) => !objects.some((object) => object.piece && object.piece.col === col && object.piece.row === row));
  if (rows.length === 0) return null;
  return { x: cellX(col), y: cellY(rows[Math.floor(field.rng() * rows.length)]!) };
}

/** Phase 4 of the tick: spawn, pick up and fly the decoys of the run. */
export function stepDecoys(run: LevelRun, field: DecoyField, context: TickContext, left: number): void {
  const { boss, core } = run;
  if (!boss) return;
  const emit = (event: { type: string; [detail: string]: unknown }) => context.emit(event);
  const fighting = boss.status === 'phase' || boss.status === 'transition';

  // The fight is over (retreat) or has not started: nothing spawns and the bags on screen are gone.
  if (!fighting && boss.status !== 'entering') field.decoys = field.decoys.filter((decoy) => decoy.state === 'in-flight');
  if (fighting) field.spawnTicks += 1;

  const onScreen = field.decoys.length;
  if (fighting && onScreen < DECOY_MAX_ON_SCREEN && field.spawnTicks >= DECOY_SPAWN_TICKS) {
    const x = left + DECOY_SPAWN_FROM * VIEW_WIDTH + field.rng() * (1 - DECOY_SPAWN_FROM) * VIEW_WIDTH;
    // Among the portrait walls of JEFE-2 or the check columns of JEFE-3 the decoy spawns inside the gap of the nearest one
    // (reachable, §9.1); on the board of JEFE-3 it spawns in a free cell of its board column.
    const anchor = run.attacks ? gapAnchor(run.attacks, x, core.tick) : null;
    const cell = !anchor && run.attacks && boss.def.phases[boss.phase - 1]?.pattern === 'plazas' ? freeCell(run.attacks.objects, x, field) : null;
    const spawnX = anchor ? anchor.x : cell ? cell.x : x;
    const y = anchor ? anchor.y : cell ? cell.y : spawnHeight(run, field, x);
    field.decoys.push({ state: 'on-screen', x: spawnX, y, fromX: spawnX, fromY: y, ticksToImpact: DECOY_FLIGHT_TICKS });
    field.spawned.push({ x: spawnX, y });
    field.spawnTicks = 0;
    emit({ type: 'decoy:spawn', x: spawnX, y });
  }

  // Pick up: touching counts (circle of PICKUP_RADIUS against the box of the plane, GDD §7).
  const plane = createAabb(core.player.x, core.player.y, PLAYER_HITBOX.width, PLAYER_HITBOX.height);
  for (const decoy of field.decoys) {
    if (decoy.state === 'on-screen' && isCollectable(plane, decoy)) {
      decoy.state = 'in-flight';
      decoy.fromX = decoy.x;
      decoy.fromY = decoy.y;
      emit({ type: 'decoy:pickup', x: decoy.x, y: decoy.y });
    }
  }

  // One tick of flight: the decoy always aims at where the boss is now and arrives on tick 48.
  const remaining: Decoy[] = [];
  for (const decoy of field.decoys) {
    if (decoy.state === 'on-screen') {
      // It scrolls with the world; once past the left edge it is gone and the counter restarts.
      if (decoy.x < left - LEFT_MARGIN) {
        field.spawnTicks = 0;
        continue;
      }
      remaining.push(decoy);
      continue;
    }
    decoy.ticksToImpact -= 1;
    if (decoy.ticksToImpact > 0) {
      const t = 1 - decoy.ticksToImpact / DECOY_FLIGHT_TICKS;
      decoy.x = decoy.fromX + (boss.x - decoy.fromX) * t;
      decoy.y = decoy.fromY + (boss.y - decoy.fromY) * t;
      remaining.push(decoy);
      continue;
    }
    field.spawnTicks = 0;
    if (boss.status === 'phase' && isVulnerable(run)) {
      hitBossInRun(run, context);
      field.lastHitTick = core.tick;
      core.pendingScore += DECOY_HIT_SCORE;
      emit({ type: 'decoy:hit', bossId: boss.def.id, hp: boss.hp });
      emit({ type: 'boss:reaction', bossId: boss.def.id, textKey: DECOY_REACTION_KEY });
    } else {
      emit({ type: 'decoy:miss', bossId: boss.def.id });
      // El Chato hidden in his tunnel (#133): the bag is lost with a comic reaction and the 6 s counter goes on.
      if (boss.status === 'phase') emit({ type: 'boss:reaction', bossId: boss.def.id, textKey: DECOY_MISS_KEY });
    }
  }
  field.decoys = remaining;
}
